<!--
  The workspace's site-visit card: the product chips, then this site's live site visits as the SAME row
  the cross-project scheduling queue renders.

  🔴 No state, label or date is derived here. The row's rendering belongs to
  <app-scheduling-queue-row>, which derives everything from the ported transition table — so a visit
  cannot read one way on the scheduling page and another way in the workspace. The inline three-date
  form that used to live here is gone with the legacy `proposal.siteVisit1..3` fields it read;
  submitting availability is now the row's own pending action, which opens the negotiation panel below.
-->
<div class="scheduling-card" data-testid="scheduling-card">
  <!-- Which products this one visit applies to (the requiring set). Kept above the rows: the workspace
       is organized around products, and the link between a visit and the products it covers is not
       something the site-visit state itself carries. -->
  @if (requiringCount > 0) {
  <div class="scheduling-card__applies" data-testid="scheduling-applies">
    <span class="scheduling-card__applies-label">Visit applies to</span>
    <div class="scheduling-card__chips">
      @for (product of appliesTo; track $index) {
      <span
        class="chip scheduling-card__applies-chip"
        [attr.data-testid]="'scheduling-applies-' + product.testId"
        >{{ product.title }}</span
      >
      }
    </div>
  </div>
  }

  @if (loading) {
  <div class="empty-state" data-testid="scheduling-loading">Loading site visit…</div>
  } @else if (loadFailed) {
  <div class="empty-state" data-testid="scheduling-error">
    This site visit couldn't be loaded. Reload the page to try again.
  </div>
  } @else if (hasRows) { @for (vm of rowVms; track trackByVisitId($index, vm)) {
  <!--
    🔴 `(reschedule)` is wired here as well as on the scheduling page, and it has to be: the two
    surfaces render the SAME row, so a control that skips a click on one and not the other is the exact
    divergence this component exists to prevent. `openForReschedule` opens the row as `open` does and
    additionally records the intent, which the panel below reads once on init.
  -->
  <app-scheduling-queue-row
    [vm]="vm"
    [open]="isOpen(vm)"
    [readOnly]="readOnly"
    (action)="open(vm)"
    (toggle)="open(vm)"
    (reschedule)="openForReschedule(vm)"
  >
    <!--
      The opened row's detail, projected in. 🔴 The `@if` is here and not inside the row: projected
      content is built in the view that declares it, so guarding inside the component would instantiate
      a <site-visit-negotiation> for every row rather than only the open one.
    -->
    @if (isOpen(vm)) {
    <div class="queue-row__detail" [attr.data-testid]="vm.testId + '-detail'">
      @if (detailLoading) {
      <p class="queue-row__detail-note" [attr.data-testid]="vm.testId + '-detail-loading'">
        Loading this site visit…
      </p>
      } @else if (detailFailed) {
      <p class="queue-row__detail-note" [attr.data-testid]="vm.testId + '-detail-error'">
        This site visit couldn't be loaded. Reload the page to try again.
      </p>
      } @else if (detail) {
      <!-- Every state's interaction, unchanged, from the component the scheduling queue and the
           per-project site-visit area already use. -->
      <site-visit-negotiation
        [visit]="detail"
        [identityInHost]="true"
        [readOnly]="readOnly"
        [startRescheduleExpanded]="rescheduleIntent"
        (visitChange)="onVisitChange($event)"
      ></site-visit-negotiation>
      }
    </div>
    }
  </app-scheduling-queue-row>
  } } @else if (notRequired) {
  <!-- Terminal: no product on this site requires a visit. -->
  <div class="empty-state" data-testid="scheduling-not-required">
    No site visit is required for this order.
  </div>
  } @else {
  <!--
    A product on this site requires a visit and neither read found one: not in the live queue, and no
    negotiation on the site-scoped read either. Deliberately a different sentence from "no visit is
    required", which would be false here. A completed or cancelled visit does NOT land here — the
    queue excludes those two states server-side and the card falls back to the site-scoped read for
    exactly that case, so the /project-history/:id record renders its row rather than this note.
  -->
  <div class="empty-state" data-testid="scheduling-none">
    No site visit to display.
  </div>
  }
</div>
